/**
 * Sites the user may report at, kept on the device for offline reporting
 * (ADR-0029). Frontline officers have no survey pack, so this list (from
 * GET /events/sites, with each site's extra-question templates) is their only
 * one. Falls back to the survey pack's sites before the first download.
 */
import { useEffect, useState } from 'react';
import type { Template } from '@sss/form-engine';
import { useOnline, usePackSites } from '../../app/hooks';
import { useServices } from '../../app/services';
import type { PackSite } from '../../lib/api/types';

export interface EventSite extends Omit<PackSite, 'templates'> {
  templates?: Partial<Record<'HAZARD' | 'INCIDENT', string>>;
}
export interface EventSiteList {
  sites: EventSite[];
  templates: Record<string, Template>;
}

const META_KEY = 'events.sites';

export function useEventSites(): { data: EventSiteList | undefined; loading: boolean } {
  const { api, storage } = useServices();
  const online = useOnline();
  const pack = usePackSites();
  const [list, setList] = useState<EventSiteList | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    void storage.getMeta<EventSiteList>(META_KEY).then((cached) => {
      if (live) setList((cur) => cur ?? cached ?? null);
    });
    return () => {
      live = false;
    };
  }, [storage]);

  useEffect(() => {
    if (!online) return;
    let live = true;
    api
      .get<EventSiteList>('/events/sites')
      .then(async (fresh) => {
        await storage.setMeta(META_KEY, fresh);
        if (live) setList(fresh);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [api, storage, online]);

  if (list) return { data: list, loading: false };
  if (list === null && pack.data) return { data: { sites: pack.data.map(({ templates: _t, ...s }) => s), templates: {} }, loading: false };
  return { data: undefined, loading: true };
}
